import React, { useContext } from "react";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import Markdown from "../../Markdown";
import Countup from "react-countup";
import {
  Box,
  Typography,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import EmailModal from "./EmailModal";
import Grid from "@material-ui/core/Grid";
import { ApiResponse } from "../types";
import Link from "next/link";
import { TextContext } from "@pages/bereken-bijdrage";
import LinkToExternalPage from "@components/navigation/helpers/LinkToExternalPage";

const numberOfDecimals = 2;
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    heading: {
      fontSize: theme.typography.pxToRem(15),
      fontWeight: theme.typography.fontWeightRegular,
    },
    negativeNumber: {
      color: "red",
      paddingLeft: "0",
    },
    result: {
      fontWeight: "bold",
      paddingLeft: "0",
    },
    subCategory: {
      paddingLeft: "24px",
    },
  })
);

type ResultProps = {
  openModal: any;
  handleOpenModal: any;
  handleCloseModal: any;
  onSubmitEmail: any;
  handleSubmit: any;
  register: any;
  getValues: any;
  errors: any;
  resultZIB: ApiResponse | null;
};
const Result = (props: ResultProps) => {
  const text = useContext(TextContext);
  const resultZIB = props?.resultZIB as ApiResponse;
  const classes = useStyles();
  return (
    <Paper
      style={{
        width: "100%",
        marginLeft: "auto",
        marginRight: "auto",
        marginBottom: "30px",
        marginTop: "20px",
        minWidth: "280px",
        maxWidth: "800px",
        padding: "4%",
      }}
    >
      <Box margin="20px">
        <Typography variant="h5" align="center">
          {text.socialContributionResult}
        </Typography>
        <Typography
          align="center"
          variant="h5"
          color="secondary"
          style={{ margin: "7px 0px 7px 0px" }}
        >
          €
          <Countup
            end={resultZIB.selfEmployedSocialSecurityContribution / 4}
            duration={1.5}
            separator=" "
            decimals={numberOfDecimals}
            decimal=","
          />{" "}
          / {text.quarter}
        </Typography>
        <Typography variant="subtitle1" align="center">
          €
          <Countup
            decimal=","
            end={resultZIB.selfEmployedSocialSecurityContribution}
            duration={1.5}
            separator=" "
            decimals={numberOfDecimals}
          />{" "}
          / {text.year}
        </Typography>
      </Box>
      <Box marginTop="20px" display="block">
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography className={classes.heading}>
              {text.calculationDetails}
            </Typography>
          </AccordionSummary>
          <AccordionDetails style={{ display: "block" }}>
            <Markdown>{text.calculationDetailsText}</Markdown>
            <Table
              aria-label="simple table"
              style={{ margin: "50px 0px 50px 0px" }}
            >
              <TableBody>
                <TableRow key="row1">
                  <TableCell component="th" scope="row">
                    {text.grossTaxableIncome}
                  </TableCell>
                  <TableCell align="right" style={{ width: "150px" }}>
                    €
                    <Countup
                      end={resultZIB.oneManBusinessGrossIncome}
                      duration={1.5}
                      separator=" "
                      decimals={numberOfDecimals}
                      decimal=","
                    />
                  </TableCell>
                </TableRow>
                <TableRow key="row2">
                  <TableCell component="th" scope="row">
                    {text.socialSecurityContribution}
                  </TableCell>
                  <TableCell align="right">
                    €
                    <Countup
                      end={resultZIB.selfEmployedSocialSecurityContribution}
                      duration={1.5}
                      separator=" "
                      decimal=","
                      decimals={numberOfDecimals}
                    />
                  </TableCell>
                </TableRow>
                <TableRow key="row3">
                  <TableCell component="th" scope="row">
                    {text.deductibleExpenses}
                  </TableCell>
                  <TableCell align="right">
                    €
                    <Countup
                      end={resultZIB.oneManBusinessActualCost}
                      duration={1.5}
                      separator=" "
                      decimals={numberOfDecimals}
                      decimal=","
                    />
                  </TableCell>
                </TableRow>
                <TableRow key="row4">
                  <TableCell component="th" scope="row">
                    <Markdown>{text.netTaxableProfessionalIncome}</Markdown>
                  </TableCell>
                  <TableCell className={classes.result} align="right">
                    €
                    <Countup
                      end={resultZIB.selfEmployedTaxableIncome}
                      duration={1.5}
                      separator=" "
                      decimals={numberOfDecimals}
                      decimal=","
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <div
              style={{
                textAlign: "center",
                marginTop: "40px",
                marginBottom: "30px",
              }}
            >
              {" "}
              <Button
                onClick={props.handleOpenModal}
                variant="contained"
                color="secondary"
                fullWidth={false}
                style={{ height: "100%" }}
              >
                {text.download}
              </Button>
            </div>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box marginTop="20px">
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <Typography className={classes.heading}>
              {text.importantToKnow}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div>
              <Markdown>{text.moreInfoTabText}</Markdown>{" "}
              <div
                style={{
                  textAlign: "center",
                  marginTop: "40px",
                  marginBottom: "30px",
                }}
              >
                <Link href="/bepaal-statuut" passHref>
                  <Button color="secondary" variant="contained">
                    {text.check}
                  </Button>
                </Link>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      </Box>
      <Box marginTop="40px">
        <Markdown>{text.contributionDoesNotMatch}</Markdown>
      </Box>
      <div style={{ marginTop: "40px" }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <LinkToExternalPage url={text.contributionDoesNotMatchButtonURL}>
              <Button
                variant="contained"
                color="primary"
                fullWidth={true}
                style={{ height: "100%" }}
              >
                {text.contributionDoesNotMatchButton}
              </Button>
            </LinkToExternalPage>
          </Grid>
          <Grid item xs={12} sm={6}>
            <LinkToExternalPage url={text.moreInfoButtonURL}>
              <Button
                variant="contained"
                color="primary"
                fullWidth={true}
                style={{ height: "100%" }}
              >
                {text.moreInfoButton}
              </Button>
            </LinkToExternalPage>
          </Grid>
        </Grid>
      </div>
      <Box margin="10px" display="flex" alignItems="stretch">
        <EmailModal
          handleCloseModal={props.handleCloseModal}
          openModal={props.openModal}
          onSubmitEmail={props.onSubmitEmail}
          handleSubmit={props.handleSubmit}
          register={props.register}
          getValues={props.getValues}
          errors={props.errors}
          // text={props.text}
        />
      </Box>
    </Paper>
  );
};

export default Result;
